import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from 'antd';
import { UploadFile } from 'antd/lib';
import cs from 'classnames';

import Alert from 'components/Alert/Alert';
import AttachmentsComponent from 'components/AttachmentsComponent/AttachmentsComponent';
import StyledSwitch from 'components/StyledSwitch';
import UploadFileControl from 'components/UploadFileControl';

import {
  selectActiveApplication,
  selectActiveUserId
} from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';

import useErrorCatch from 'hooks/useErrorCatch';
import usePageviewEvent from 'hooks/usePageViewEvent';
import { useSyncApplicationRoute } from 'hooks/useSyncApplicationRoute';

import { GTMEventLogger } from 'utils/analytics';
import { getLatestAttachmentOfType } from 'utils/getLatestAttachmentOfType';
import { notify } from 'utils/toast/createToast';

import { ApplicationWithOfferLetter } from 'types/applications';

import {
  AlertType,
  ApplicationTags,
  ClientApplicationStages,
  GTMEvents,
  JobseekerAttachmentTypes,
  OfferLetterSteps
} from 'constants/enums';
import { offerLetterFileTypes } from 'constants/jobseekerSettings';
import { TOUR_STEP_TARGET_APPLICATION_OFFER_TAB } from 'constants/tour';

import AlertsBlock from './AlertsBlock/AlertsBlock';
import OfferLetterDetails from './OfferLetterDetails/OfferLetterDetails';
import styles from './OfferView.module.scss';
import OfferActionButtonWrapper from './components/OfferActionButtonWrapper/OfferActionButtonWrapper';
import { getTitle } from './utils/getTitle';

interface OfferViewProps {
  isLoading: boolean;
  fetchApplicationData: (applicationId: string) => Promise<void>;
}

const ApplicationTagsAllowed = [
  ApplicationTags.AcceptedApplicationStageOfferLetterSigned,
  ApplicationTags.DeclinedApplicationStageJobseekerRefused,
  ApplicationTags.DeclinedApplicationStageWithdrawn,
  ApplicationTags.HiredApplicationStageOfferLetterSigned
];

const OfferView: FC<OfferViewProps> = ({
  isLoading,
  fetchApplicationData
}: OfferViewProps) => {
  const catchError = useErrorCatch();
  const { t } = useTranslation();

  const [isOfferLetterLoading, setIsOfferLetterLoading] = useState(false);
  const [applicationWithOfferLetter, setApplicationWithOfferLetter] =
    useState<ApplicationWithOfferLetter | null>(null);

  const applicationId = useSelector(selectActiveUserId);

  const application = useSelector(selectActiveApplication);

  const [isCustomLetter, setIsCustomLetter] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [offerFile, setOfferFile] = useState<UploadFile | null>(null);
  const [step, setStep] = useState<OfferLetterSteps>(
    OfferLetterSteps.OfferLetterNotSent
  );

  useSyncApplicationRoute(applicationId);
  usePageviewEvent(GTMEvents.ApplicationsListViewSidebarOffer);

  const offerLetterAttachment = useMemo(() => {
    return getLatestAttachmentOfType(
      application?.attachments,
      JobseekerAttachmentTypes.OfferLetter
    );
  }, [application]);

  const isApplicationDataFetchAllowed =
    applicationId &&
    ApplicationTagsAllowed.some((applicationTag) =>
      application?.tags?.includes(applicationTag)
    );

  const getApplicationData = async (applicationId: string) => {
    if (!isApplicationDataFetchAllowed) {
      return;
    }

    setIsOfferLetterLoading(true);
    try {
      const applicationWithOfferLetter =
        await applicationsService.getApplicationWithOfferLetter(applicationId);
      setApplicationWithOfferLetter(applicationWithOfferLetter);
    } catch (error) {
      setApplicationWithOfferLetter(null);
    } finally {
      setIsOfferLetterLoading(false);
    }
  };

  useEffect(() => {
    getApplicationData(applicationId);
  }, [applicationId, application?.clientStage]);

  useEffect(() => {
    if (offerLetterAttachment && application?.offerId) {
      setStep(OfferLetterSteps.OfferLetterSent);
    } else {
      setStep(OfferLetterSteps.OfferLetterNotSent);
    }
  }, [offerLetterAttachment, application]);

  const handleAttachmentChange = () => {
    if (!applicationId) {
      return;
    }
    fetchApplicationData(applicationId);
  };

  const handleGenerateAndSendOfferLetter = async (
    uploadFile?: UploadFile | null
  ) => {
    try {
      setIsSending(true);
      const formData = new FormData();

      if (uploadFile) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formData.append('attachment', uploadFile as any);
      }

      await applicationsService.generateAndSendApplicationOfferLetter(
        applicationId,
        formData
      );
      notify({
        type: AlertType.Success,
        description: t('sidebar.application.offer.sendOffer.success', {
          name: application?.jobseekerFullName
        })
      });

      fetchApplicationData(applicationId);

      GTMEventLogger(GTMEvents.ApplicationsListViewSidebarOfferJoblioSent);
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.offerFailed')
        }
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleWithdrawOfferLetter = async () => {
    try {
      if (!application?.offerId) {
        return;
      }

      setIsSending(true);

      await applicationsService.withdrawApplicationOfferLetter({
        applicationId,
        offerLetterId: application.offerId
      });
      notify({
        type: AlertType.Success,
        description: t('sidebar.application.offer.withdrawOffer.success')
      });

      fetchApplicationData(applicationId);

      GTMEventLogger(GTMEvents.ApplicationsListViewSidebarOfferCancel);
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.failedToWithdrawOffer')
        }
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleChangeCustomLetterMode = (isCustom: boolean) => {
    setIsCustomLetter(isCustom);

    if (isCustom) {
      setStep(OfferLetterSteps.CustomOfferUpload);

      GTMEventLogger(GTMEvents.ApplicationsListViewSidebarOfferCustom);
      return;
    }

    setStep(OfferLetterSteps.OfferLetterNotSent);

    GTMEventLogger(GTMEvents.ApplicationsListViewSidebarOfferJoblio);
  };

  const handleLocalFileUpload = (file: UploadFile | null) => {
    setOfferFile(file);
  };

  const handleSendCustomOffer = () => {
    handleGenerateAndSendOfferLetter(offerFile);

    GTMEventLogger(GTMEvents.ApplicationsListViewSidebarOfferCustomSend);
  };

  const isSendOfferAllowed =
    application?.clientStage ===
    ClientApplicationStages.ClientReviewApplicationStageClient;

  const isShowFileUnavailable = application?.tags?.includes(
    ApplicationTags.DeclinedApplicationStageWithdrawn
  );

  const isShowSendOfferButton =
    step === OfferLetterSteps.OfferLetterNotSent &&
    application?.clientStage !==
      ClientApplicationStages.DeclinedApplicationStageClient;

  const isShowCancelOfferButton =
    step === OfferLetterSteps.OfferLetterSent &&
    !application?.tags?.includes(
      ApplicationTags.DeclinedApplicationStageJobseekerRefused
    ) &&
    !application?.tags?.includes(
      ApplicationTags.HiredApplicationStageOfferLetterSigned
    );

  const isShowCustomOfferSwitch =
    ![
      OfferLetterSteps.WithdrawOfferConfirm,
      OfferLetterSteps.OfferLetterSent
    ].includes(step) &&
    application?.clientStage !==
      ClientApplicationStages.DeclinedApplicationStageClient;

  const isClientReviewApplication =
    application?.clientStage ===
    ClientApplicationStages.ClientReviewApplicationStageClient;

  const isDeclinedApplicationStage =
    application?.clientStage ===
    ClientApplicationStages.DeclinedApplicationStageClient;

  return (
    <aside>
      {isLoading || isOfferLetterLoading ? (
        <div>{t('common.loading')}</div>
      ) : (
        <>
          <div id={TOUR_STEP_TARGET_APPLICATION_OFFER_TAB}>
            <h2 className={styles.title}>
              {t(
                getTitle(
                  application?.tags,
                  isClientReviewApplication,
                  isDeclinedApplicationStage
                )
              )}
            </h2>

            <AlertsBlock
              step={step}
              offerLetterAttachment={offerLetterAttachment}
              application={application}
            />

            {isShowCustomOfferSwitch && (
              <section className={styles.switch}>
                <div className={styles.label}>
                  {t('sidebar.application.offer.type')}
                </div>
                <StyledSwitch
                  leftLabel={t('sidebar.application.offer.switch.left')}
                  rightLabel={t('sidebar.application.offer.switch.right')}
                  onChange={handleChangeCustomLetterMode}
                  isDefaultChecked={isCustomLetter}
                  disabled={isSending || !isSendOfferAllowed}
                />
              </section>
            )}
          </div>

          {step === OfferLetterSteps.CustomOfferUpload && (
            <Alert
              type={AlertType.Info}
              message={t('sidebar.application.offer.uploadOffer')}
              className={styles.alert}
            />
          )}

          <section
            className={cs({ [styles.attachment]: offerLetterAttachment })}
          >
            <AttachmentsComponent
              attachmentType={JobseekerAttachmentTypes.OfferLetter}
              attachmentList={
                offerLetterAttachment ? [offerLetterAttachment] : []
              }
              onChange={handleAttachmentChange}
              allowedFileTypes={offerLetterFileTypes}
              isDeleteDisabled
              isPreviewFile
            />
          </section>

          {isShowFileUnavailable && (
            <div className={styles.fileUnavailable}>
              {t('sidebar.application.offer.fileUnavailable')}
            </div>
          )}

          {step === OfferLetterSteps.CustomOfferUpload && (
            <section>
              <UploadFileControl
                isRoundCorners
                onChangeFile={handleLocalFileUpload}
                allowedFileTypes={offerLetterFileTypes}
                label={
                  offerFile ? t('common.replaceFile') : t('common.uploadFile')
                }
              />
            </section>
          )}

          <section className={styles.content}>
            {step === OfferLetterSteps.OfferLetterNotSent &&
              isSendOfferAllowed &&
              !isSending && (
                <Alert
                  type={AlertType.Info}
                  message={t('sidebar.application.offer.offerGeneratedAuto')}
                  className={styles.alert}
                />
              )}

            <OfferLetterDetails
              applicationWithOfferLetter={applicationWithOfferLetter}
              applicationTags={application?.tags}
            />

            {isShowCancelOfferButton && (
              <Button
                type="link"
                className={cs(styles.btnFullWidth, styles.btnRed)}
                onClick={() => setStep(OfferLetterSteps.WithdrawOfferConfirm)}
                disabled={isSending}
              >
                {t('sidebar.application.offer.cancelOffer')}
              </Button>
            )}

            {step === OfferLetterSteps.WithdrawOfferConfirm && (
              <div className={styles.buttons}>
                <Button
                  className={cs(styles.btnRed)}
                  onClick={() => setStep(OfferLetterSteps.OfferLetterSent)}
                >
                  {t('common.back')}
                </Button>

                <Button
                  type="primary"
                  className={cs(styles.btnRed, styles.btnRedFill)}
                  onClick={handleWithdrawOfferLetter}
                  loading={isSending}
                >
                  {t('sidebar.application.offer.cancelOffer')}
                </Button>
              </div>
            )}

            {isShowSendOfferButton && (
              <OfferActionButtonWrapper isEnabled={isSendOfferAllowed}>
                <Button
                  type="primary"
                  className={styles.btnFullWidth}
                  disabled={!isSendOfferAllowed || isSending}
                  onClick={() => handleGenerateAndSendOfferLetter()}
                  loading={isSending}
                >
                  {t('sidebar.application.offer.sendOffer')}
                </Button>
              </OfferActionButtonWrapper>
            )}

            {step === OfferLetterSteps.CustomOfferUpload && (
              <Button
                type="primary"
                className={styles.btnFullWidth}
                disabled={!offerFile}
                onClick={handleSendCustomOffer}
                loading={isSending}
              >
                {t('sidebar.application.offer.sendOffer')}
              </Button>
            )}
          </section>
        </>
      )}
    </aside>
  );
};

export default OfferView;
